import type { ApiErrorBody, AuthTokens, AuthUser, BreakProject } from '@chronos/shared';

/**
 * Resolves the API base URL. Electron injects `window.chronos.apiBaseUrl` at
 * runtime so the same web bundle can be served from file:// in the desktop app.
 */
export function apiBaseUrl(): string {
  // Electron's preload sets this before any page script evaluates.
  const injected = (globalThis as { chronos?: { apiBaseUrl?: string } }).chronos?.apiBaseUrl;
  if (injected) return injected.replace(/\/$/, '');
  const fromEnv = import.meta.env.VITE_API_URL as string | undefined;
  if (fromEnv) return fromEnv.replace(/\/$/, '');
  return 'http://localhost:4000';
}

const ACCESS_KEY = 'chronos.accessToken';
const REFRESH_KEY = 'chronos.refreshToken';

export const tokenStore = {
  get access(): string | null {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh(): string | null {
    return localStorage.getItem(REFRESH_KEY);
  },
  set(tokens: Pick<AuthTokens, 'accessToken' | 'refreshToken'>): void {
    this.setLocal(tokens);
    // Keep the desktop main process in sync so background uploads stay authorised.
    void (globalThis as { chronos?: { setTokens?: (t: unknown) => void } }).chronos?.setTokens?.(tokens);
  },
  /** Stores a pair that came *from* the main process, without echoing it back. */
  setLocal(tokens: Pick<AuthTokens, 'accessToken' | 'refreshToken'>): void {
    localStorage.setItem(ACCESS_KEY, tokens.accessToken);
    localStorage.setItem(REFRESH_KEY, tokens.refreshToken);
  },
  clear(): void {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    void (globalThis as { chronos?: { clearTokens?: () => void } }).chronos?.clearTokens?.();
  },
};

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type Listener = () => void;
const unauthorizedListeners = new Set<Listener>();

/** Fired when the session cannot be recovered, so the app can bounce to login. */
export function onUnauthorized(fn: Listener): () => void {
  unauthorizedListeners.add(fn);
  return () => unauthorizedListeners.delete(fn);
}

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Exchanges the refresh token for a new pair. Concurrent 401s share one
 * in-flight refresh so a page full of queries cannot burn the rotation chain.
 */
async function refreshTokens(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    // Inside the desktop app the main process is the sole refresher. Two
    // holders of one rotating refresh token would trip the server's reuse
    // detection and log both out — which is exactly what used to happen.
    const bridge = (globalThis as { chronos?: { refreshTokens?: (stale: string | null) => Promise<{ accessToken: string; refreshToken: string } | null> } }).chronos;
    if (bridge?.refreshTokens) {
      try {
        const fresh = await bridge.refreshTokens(tokenStore.access);
        if (!fresh) return false;
        tokenStore.setLocal(fresh);
        return true;
      } catch {
        return false;
      } finally {
        setTimeout(() => {
          refreshInFlight = null;
        }, 0);
      }
    }

    const refreshToken = tokenStore.refresh;
    if (!refreshToken) return false;
    try {
      const res = await fetch(`${apiBaseUrl()}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return false;
      const data = (await res.json()) as AuthTokens;
      tokenStore.set(data);
      return true;
    } catch {
      return false;
    } finally {
      // Cleared on the next tick so callers awaiting this promise all see it.
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();

  return refreshInFlight;
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null | string[]>;
  /** Skips the Authorization header (login, password reset, …). */
  anonymous?: boolean;
  signal?: AbortSignal;
  raw?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(`${apiBaseUrl()}${path}`);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) value.forEach((v) => url.searchParams.append(key, v));
    else url.searchParams.set(key, String(value));
  }
  return url.toString();
}

async function raise(res: Response): Promise<never> {
  let body: ApiErrorBody | undefined;
  try {
    body = (await res.json()) as ApiErrorBody;
  } catch {
    /* non-JSON error body */
  }
  throw new ApiError(res.status, body?.error?.code ?? 'HTTP_ERROR', body?.error?.message ?? res.statusText, body?.error?.details);
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, anonymous, signal } = options;

  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (body !== undefined && !(body instanceof FormData)) headers['Content-Type'] = 'application/json';
    if (!anonymous && tokenStore.access) headers.Authorization = `Bearer ${tokenStore.access}`;

    return fetch(buildUrl(path, query), {
      method,
      headers,
      body: body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body),
      signal,
    });
  };

  let res = await send();

  // One transparent retry after refreshing an expired access token.
  if (res.status === 401 && !anonymous && tokenStore.refresh) {
    if (await refreshTokens()) {
      res = await send();
    } else {
      tokenStore.clear();
      unauthorizedListeners.forEach((fn) => fn());
    }
  }

  if (res.status === 401 && !anonymous) {
    unauthorizedListeners.forEach((fn) => fn());
  }

  if (!res.ok) await raise(res);
  if (res.status === 204) return undefined as T;
  if (options.raw) return (await res.blob()) as T;

  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** Downloads a file through the authenticated fetch path and saves it. */
export async function downloadFile(path: string, query: RequestOptions['query'], filename: string): Promise<void> {
  const blob = await apiFetch<Blob>(path, { query, raw: true });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export interface MeResponse {
  user: AuthUser;
  /** Resolved break project, or null when the admin has not configured one. */
  breakProject: BreakProject | null;
  webBaseUrl?: string;
  settings: {
    screenshotIntervalMinutes: number;
    idleTimeoutMinutes: number;
    stopTimerOnIdle: boolean;
    allowManualTimeEntry: boolean;
    monitoringNoticeVersion: string;
    breakProjectId: string | null;
    casualLeaveNoticeWorkingDays: number;
    autoApproveMedicalLeave: boolean;
  };
}
